/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import WysiwygIcon from '@mui/icons-material/Wysiwyg';
import { AdasProgramsReportPages } from './ItemDetails/common/constants';

const baseFetchDatasetsEndpoint = '/HttpTriggerAdasPrograms?page=coverageRequirements&requestType=fetchRedlineDatasets'
const baseEndpoint = '/HttpTriggerAdasPrograms'


const createAdasProgramsItemDetailsPageLink = (adasProgramId: string, programName: string, modelYear: string, tabName: string) => {
  const url = `${ADAS_PROGRAMS_CONSTANTS.itemDetailsRoute}?adasProgramId=${adasProgramId}&programName=${programName}&modelYear=${modelYear}
  &${ADAS_PROGRAMS_CONSTANTS.itemDetails.horizontalTabs.pageHtabs.tabNameUrlKey}=${tabName}`
  // encode url
  const encodedUrl = encodeURI(url)
  return encodedUrl
}


export const getFullSensorLocationNameGivenAbbreviation = (locationAbbreviation: string) => {
  const location = LOCATION_OPTIONS.find(location => location.locationKey === locationAbbreviation)
  if (location) {
    return location.sensorLocation
  }
  return locationAbbreviation
}


export const getSensorLocationAbbreviationGivenFullLocationName = (fullLocationName: string) => {
  const location = LOCATION_OPTIONS.find(location => location.sensorLocation === fullLocationName)
  if (location) {
    return location.locationKey
  }
  return fullLocationName
}


/**
 * All possible ADAS Program sensor locations and their keys.
 * 
 * @property {string} sensorLocation - Human-readable sensor location name.
 * @property {string} locationKey - The key associated with the sensor location.
 */
export const LOCATION_OPTIONS = [
  {
    sensorLocation: 'Front Center',
    locationKey: 'FC'
  },
  {
    sensorLocation: 'Front Left Mid',
    locationKey: 'FLM'
  },
  {
    sensorLocation: 'Front Right Mid',
    locationKey: 'FRM'
  },

  {
    sensorLocation: 'Front Left Fender',
    locationKey: 'FLF'
  },
  {
    sensorLocation: 'Front Right Fender',
    locationKey: 'FRF'
  },

  {
    sensorLocation: 'Front Left Corner',
    locationKey: 'FLC'
  },
  {
    sensorLocation: 'Front Right Corner',
    locationKey: 'FRC'
  },
  {
    sensorLocation: 'Side Left Mirror',
    locationKey: 'SLM'
  },
  {
    sensorLocation: 'Side Right Mirror',
    locationKey: 'SRM'
  },
  {
    sensorLocation: 'Side Left A-pillar',
    locationKey: 'SLA'
  },
  {
    sensorLocation: 'Side Right A-pillar',
    locationKey: 'SRA'
  },

  {
    sensorLocation: 'Headliner Front Center',
    locationKey: 'HFC'
  },
  {
    sensorLocation: 'Headliner Front Left',
    locationKey: 'HFL'
  },
  {
    sensorLocation: 'Headliner Front Right',
    locationKey: 'HFR'
  },
  {
    sensorLocation: 'Headliner Front Center Left',
    locationKey: 'HFCL'
  },
  {
    sensorLocation: 'Headliner Front Center Right',
    locationKey: 'HFCR'
  },


  {
    sensorLocation: 'Side Left B-pillar',
    locationKey: 'SLB'
  },
  {
    sensorLocation: 'Side Right B-pillar',
    locationKey: 'SRB'
  },
  {
    sensorLocation: 'Headliner Side Left',
    locationKey: 'HSL'
  },
  {
    sensorLocation: 'Headliner Side Right',
    locationKey: 'HSR'
  },
  {
    sensorLocation: 'Side Left C-pillar',
    locationKey: 'SLC'
  },
  {
    sensorLocation: 'Side Right C-pillar',
    locationKey: 'SRC'
  },


  {
    sensorLocation: 'Headliner Rear Left',
    locationKey: 'HRL'
  },
  {
    sensorLocation: 'Headliner Rear Center Left',
    locationKey: 'HRCL'
  },
  {
    sensorLocation: 'Headliner Rear Center',
    locationKey: 'HRC'
  },
  {
    sensorLocation: 'Headliner Rear Center Right',
    locationKey: 'HRCR'
  },
  {
    sensorLocation: 'Headliner Rear Right',
    locationKey: 'HRR'
  },


  {
    sensorLocation: 'Rear Left Fender',
    locationKey: 'RLF'
  },
  {
    sensorLocation: 'Rear Right Fender',
    locationKey: 'RRF'
  },


  {
    sensorLocation: 'Rear Left Corner',
    locationKey: 'RLC'
  },
  {
    sensorLocation: 'Rear Left Mid',
    locationKey: 'RLM'
  },

  {
    sensorLocation: 'Rear Center',
    locationKey: 'RC'
  },
  {
    sensorLocation: 'Rear Right Mid',
    locationKey: 'RRM'
  },
  {
    sensorLocation: 'Rear Right Corner',
    locationKey: 'RRC'
  },
]


const ADAS_PROGRAMS_CONSTANTS = {
  sidebar: {
    title: 'ADAS Programs',
    icon: <WysiwygIcon style={{
      width: "30px",
      height: "50px",
    }}/>
  },
  mainRoute: '/adas-programs',
  table: {},
  itemDetailsRoute: '/adas-programs-details',
  itemDetails: {
    coverageRequirements: {
      saveDatasetsForTreeEndpoint: '/HttpTriggerAdasPrograms?page=coverageRequirements&requestType=saveDatasetsForTree',
      fetchDatasetsForTreeEndpoint: '/HttpTriggerAdasPrograms?page=coverageRequirements&requestType=fetchDatasetsForTree',
      fetchDatasetsEndpoint: baseFetchDatasetsEndpoint,
      fetchFilteredDatasetsEndpoint: `${baseFetchDatasetsEndpoint}&shouldFilter=true`,
      fetchReportFormatsEndpoint: '/HttpTriggerAdasPrograms?page=coverageRequirements&requestType=fetchReportFormats',
      fetchOrSaveCoverageRequirementsMetaDataEndpoint: '/HttpTriggerAdasPrograms?page=coverageRequirements&requestType=fetchOrSaveCoverageRequirementsMetaData',
      saveFilteredDatasetsEndpoint: '/HttpTriggerAdasPrograms?page=coverageRequirements&requestType=saveFilteredDatasets',
      getSaveOrFetchDatasetsEndpoint: (requestType: string) => `/HttpTriggerAdasPrograms?page=coverageRequirements&requestType=saveFilteredDatasets&saveOrFetch=${requestType}`,
      fetchObjectsFromRedlineDatasetEndpoint: '/HttpTriggerAdasPrograms?page=coverageRequirements&requestType=fetchObjectsFromRedlineDataset',
      viewReportRoute: '/adas-programs-details/view-report',
      checkDeleteReportEndpoint: '/HttpTriggerAdasPrograms?page=coverageRequirements&requestType=checkIfDeleteReportValid',

      fetchAdasPrograms: '/HttpTriggerAdasPrograms?page=coverageRequirementsApp&requestType=fetchAdasPrograms', // This is only used in the coverage requirements app right now.
      fetchMergedDataSetsTree: '/HttpTriggerAdasPrograms?page=coverageRequirementsApp&requestType=fetchMergedDataSetsTree',
      reportTypes: new Set([188, 141]),
      folderIntItemTypes: new Set([30, 31, 32])
    },
    sensorBom: {
      saveProgramPropertiesEndpoint: '/HttpTriggerAdasPrograms?page=sensorBom&requestType=saveProgramProperties',
      fetchProgramPropertiesEndpoint: '/HttpTriggerAdasPrograms?page=sensorBom&requestType=fetchProgramProperties',
      isSensorBomReadOnly: '/HttpTriggerAdasPrograms?page=sensorBom&requestType=isSensorBomReadOnly',
      getVehIdsSensorBom: '/HttpTriggerAdasPrograms?page=sensorBom&requestType=getVehIds',
      sensorBomReadOnlyDevStagesMessage: 'BOM is read-only because it is being utilized by the following vehicle program(s): ',
      locationOptions: LOCATION_OPTIONS,
    },
    sensorComponentRequirements: {
      endpoints: {
        getReportsTable: `${baseEndpoint}?page=${AdasProgramsReportPages.SENSOR_COMPONENT_REQUIREMENT}&req=getReportsTable`,
        reorderTableRows: `${baseEndpoint}?page=${AdasProgramsReportPages.SENSOR_COMPONENT_REQUIREMENT}&req=reorderTableRows`,
        getReport: `${baseEndpoint}?page=${AdasProgramsReportPages.SENSOR_COMPONENT_REQUIREMENT}&req=getReport`,
        saveReportsTable: `${baseEndpoint}?page=${AdasProgramsReportPages.SENSOR_COMPONENT_REQUIREMENT}&req=saveReportsTable`,
        updateReportSummary: `${baseEndpoint}?page=${AdasProgramsReportPages.SENSOR_COMPONENT_REQUIREMENT}&req=updateReportSummary`,
        updateReport: `/orchestrators/DurableFunctionsAdasProgramsOrchestrator`,
        generateCombinedReport: `${baseEndpoint}?page=${AdasProgramsReportPages.SENSOR_COMPONENT_REQUIREMENT}&req=generateCombinedReport`,
        checkDeleteReport: `${baseEndpoint}?page=${AdasProgramsReportPages.SENSOR_COMPONENT_REQUIREMENT}&req=checkDeleteReport`,
        deleteReport: `${baseEndpoint}?page=${AdasProgramsReportPages.SENSOR_COMPONENT_REQUIREMENT}&req=deleteReport`,
        fetchGreenLineDataSets: `${baseEndpoint}?page=${AdasProgramsReportPages.SENSOR_COMPONENT_REQUIREMENT}&req=fetchGreenLineDataSets`,
        fetchGreenLineObjects: `${baseEndpoint}?page=${AdasProgramsReportPages.SENSOR_COMPONENT_REQUIREMENT}&req=fetchGreenLineObjects`,
      },
    },
    worldModelSolutionRequirements: {
      endpoints: {
        getReportsTable: `${baseEndpoint}?page=${AdasProgramsReportPages.WORLD_MODEL_SOLUTION_REQUIREMENT}&req=getReportsTable`,
        reorderTableRows: `${baseEndpoint}?page=${AdasProgramsReportPages.WORLD_MODEL_SOLUTION_REQUIREMENT}&req=reorderTableRows`,
        getReport: `${baseEndpoint}?page=${AdasProgramsReportPages.WORLD_MODEL_SOLUTION_REQUIREMENT}&req=getReport`,
        saveReportsTable: `${baseEndpoint}?page=${AdasProgramsReportPages.WORLD_MODEL_SOLUTION_REQUIREMENT}&req=saveReportsTable`,
        updateReportSummary: `${baseEndpoint}?page=${AdasProgramsReportPages.WORLD_MODEL_SOLUTION_REQUIREMENT}&req=updateReportSummary`,
        updateReport: `/orchestrators/DurableFunctionsAdasProgramsOrchestrator`,
        generateCombinedReport: `${baseEndpoint}?page=${AdasProgramsReportPages.WORLD_MODEL_SOLUTION_REQUIREMENT}&req=generateCombinedReport`,
        checkDeleteReport: `${baseEndpoint}?page=${AdasProgramsReportPages.WORLD_MODEL_SOLUTION_REQUIREMENT}&req=checkDeleteReport`,
        deleteReport: `${baseEndpoint}?page=${AdasProgramsReportPages.WORLD_MODEL_SOLUTION_REQUIREMENT}&req=deleteReport`,
      },
    },
    vehiclePrograms: {
      // pageLink: `${baseEndpoint}?page=vehiclePrograms`,
    },
    horizontalTabs: {
      pageHtabs: {
        tabIndexUrlKey: 'htabs-1',
        tabNameUrlKey: 'htabs-1-name',
      }
    }
  },
  landingPage: {
    checkIsDeleteProgramValid: `${baseEndpoint}?page=landingTable&requestType=shouldDeleteProgram`,
    deleteProgram: `${baseEndpoint}?page=landingTable&requestType=deleteProgram`,
    fetchPrograms: `${baseEndpoint}?page=landingTable&requestType=fetchAdasPrograms`,
  },
  linkFunctions: {
    itemDetailsMainHTabs: createAdasProgramsItemDetailsPageLink
  },
  helperFunctions: {
    formAdasProgramUrl: (adasProgramId: string) => {
      return `${ADAS_PROGRAMS_CONSTANTS.itemDetailsRoute}?adasProgramId=${adasProgramId}&${ADAS_PROGRAMS_CONSTANTS.itemDetails.horizontalTabs.pageHtabs.tabIndexUrlKey}=0`
    },
    getFullAdasProgramNameWithNameAndModelYear: (programName: string, modelYear: string) => {
      return `${programName} ${modelYear}`
    }
  }
}

// http://localhost:3000/adas-programs-details?adasProgramId=c28a1c20-9e9b-11ee-9187-000d3a01299c&htabs-1=2&htabs-1-name=Vehicle%20Programs&isGlobalDialogOpen=0&modelYear=2023&programName=ew



export default ADAS_PROGRAMS_CONSTANTS
